import {
	Component,
	computed,
	effect,
	inject,
	output,
	signal
} from '@angular/core';

import { RouterLink } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Button } from 'primeng/button';
import { userHasQuoteLogAccess } from '../../guards/quote-log.guard';
import {
	userHasEFilingAccess,
	userHasTestAccess
} from '../../guards/e-filing.guard';
import { userHasReportingAccess } from '../../guards/reporting.guard';
import { userHasPartCodingAccess } from '../../guards/part-coding.guard';
import { userHasPartCodingAdminAccess } from '../../guards/part-coding-admin.guard';
import { filter } from 'rxjs';
import { InteractionStatus } from '@azure/msal-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewportService } from '../../services/viewport.service';
import { DialogService, type DynamicDialogRef } from 'primeng/dynamicdialog';
import { AdminActionsComponent } from '../admin/admin-actions/admin-actions.component';

//component that represents the navigation bar on the left-hand side of the screen
@Component({
	selector: 'app-navigation',
	imports: [RouterLink, Button],
	templateUrl: './navigation.component.html',
	styleUrl: './navigation.component.sass',
	providers: [DialogService]
})
export class NavigationComponent {
	public readonly viewport = inject(ViewportService);
	public readonly isOpen = signal(!this.viewport.isMobile()); //toggle for if the nav bar is open
	public readonly toggleNavigation = output<boolean>();
	private msalService = inject<MsalService>(MsalService);
	private broadcastService =
		inject<MsalBroadcastService>(MsalBroadcastService);
	private ref: DynamicDialogRef | null = null;
	private dialogService = inject<DialogService>(DialogService);

	private account = signal(this.msalService.instance.getActiveAccount());
	// Controls whether the Quote Log link is shown, based on security-group membership.
	protected readonly canViewQuoteLog = computed(() =>
		userHasQuoteLogAccess(this.account())
	);

	protected readonly canViewEFiling = computed(() =>
		userHasEFilingAccess(this.msalService.instance.getActiveAccount())
	);

	// Controls whether the Reporting link is shown, based on security-group membership.
	protected readonly canViewReporting = computed(() =>
		userHasReportingAccess(this.account())
	);

	// Controls whether the Part Coding link is shown, based on partCodingUsers.
	protected readonly canViewPartCoding = computed(() =>
		userHasPartCodingAccess(this.account())
	);

	// Controls whether the Admin Part Coding link is shown, based on partCodingAdmins.
	protected readonly canViewPartCodingAdmin = computed(() =>
		userHasPartCodingAdminAccess(this.account())
	);

	protected readonly canViewTesting = computed(() =>
		userHasTestAccess(this.account())
	);

	public constructor() {
		this.broadcastService.inProgress$
			.pipe(
				filter((status) => status === InteractionStatus.None),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				this.account.set(this.msalService.instance.getActiveAccount());
			});

		// Auto-collapse the sidebar on mobile and re-expand it on desktop
		// whenever the viewport crosses the breakpoint.
		effect(() => {
			this.isOpen.set(!this.viewport.isMobile());
		});
	}

	public toggle(): void {
		this.isOpen.update((value) => !value);
		this.toggleNavigation.emit(this.isOpen());
	}

	public closeMobileNav(): void {
		if (this.viewport.isMobile()) {
			this.isOpen.set(false);
			this.toggleNavigation.emit(false);
		}
	}

	public signOut(): void {
		const account = this.msalService.instance.getActiveAccount();

		if (!account) {
			return;
		}

		void this.msalService.instance.clearCache();
		this.msalService.logoutRedirect({
			account: account,
			logoutHint: account.idTokenClaims?.login_hint ?? account.username
		});
	}

	public adminActions(): void {
		if (!this.canViewTesting()) return;

		this.ref?.close();
		this.ref = this.dialogService.open(AdminActionsComponent, {
			header: 'Admin Actions',
			width: '75%',
			modal: true,
			closable: true,
			closeOnEscape: true,
			dismissableMask: true
		});
	}
}
